import { useEffect, useCallback, useState, useRef } from "react";
import { useDocument } from "hooks/useDocument";
import { useFile } from "hooks/useFile";
import { Card, Text, Button, Table } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import tablestyles from "pages/dashboard/tablestyles.module.css";
import ImageIcon from "@rsuite/icons/Image";
import CollaspedOutlineIcon from "@rsuite/icons/CollaspedOutline";
import ExpandOutlineIcon from "@rsuite/icons/ExpandOutline";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
//import { locale } from "moment/moment";

const { Column, HeaderCell, Cell } = Table;

function TablePanel({
  title,
  collectionName,
  startUnixts,
  endUnixts,
  backgroundColor,
}) {
  const { retrieveDocs } = useDocument();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [tableData, setTableData] = useState([]);
  const totalAlerts = useRef(0);
  const noDrainingAlerts = useRef(0);
  const unintendedDrainingAlerts = useRef(0);
  const floodingAlerts = useRef(0);
  const floodingResolvedAlerts = useRef(0);

  const { getFileRef } = useFile();

  /* [2] Table */
  const [sortColumn, setSortColumn] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  // 2.1 Sample data for the table
  // 2.2 Function to sort the table data based on the current sort column and type
  const getData = () => {
    if (sortColumn && sortType) {
      return tableData.sort((a, b) => {
        let x = a[sortColumn];
        let y = b[sortColumn];
        if (typeof x === "string") {
          x = x.charCodeAt();
        }
        if (typeof y === "string") {
          y = y.charCodeAt();
        }
        if (sortType === "asc") {
          return x - y;
        } else {
          return y - x;
        }
      });
    }
    return tableData;
  };

  const handleSortColumn = (sortColumn, sortType) => {
    setTableLoading(true);
    setTimeout(() => {
      setTableLoading(false);
      setSortColumn(sortColumn);
      setSortType(sortType);
    }, 500);
  };

  const validateOperation = useCallback(async () => {
    try {
      if (startUnixts !== null && endUnixts !== null) {
        const queries = {
          whereQueries: [
            {
              field: "unixts",
              condition: ">",
              value: parseInt(startUnixts / 1000),
            },
            {
              field: "unixts",
              condition: "<=",
              value: parseInt(endUnixts / 1000),
            },
          ],
          orderByQueries: [
            {
              field: "unixts",
              direction: "desc",
            },
          ],
        };

        const retrievedDocs = await retrieveDocs(collectionName, queries);

        const formatDateTime = (jsDate) => {
          const year = jsDate.getFullYear();
          const month = String(jsDate.getMonth() + 1).padStart(2, "0"); // Months are 0-based
          const day = String(jsDate.getDate()).padStart(2, "0");
          const hours = String(jsDate.getHours()).padStart(2, "0");
          const minutes = String(jsDate.getMinutes()).padStart(2, "0");
          const seconds = String(jsDate.getSeconds()).padStart(2, "0");

          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        const tableData = retrievedDocs.map((doc, index) => ({
          id: index + 1,
          datetime: formatDateTime(doc.data.createdAt.toDate()),
          alertType:
            doc.data.alertType === "flooding"
              ? "Flooding"
              : doc.data.alertType === "no-flooding"
                ? "Flooding Resolved"
                : doc.data.alertType === "no-draining"
                  ? "No Draining"
                  : doc.data.alertType === "unintended-draining"
                    ? "Unintended Draining"
                    : null,
          sstdId: doc.data.sstdId.toUpperCase(),
          cameraId:
            "cameraId" in doc.data ? doc.data.cameraId.toUpperCase() : null,
          path: doc.data.path,
        }));
        totalAlerts.current = tableData.length;

        const alertTypes = tableData.map((obj) => obj.alertType);
        noDrainingAlerts.current = alertTypes.filter(
          (t) => t === "No Draining",
        ).length;
        unintendedDrainingAlerts.current = alertTypes.filter(
          (t) => t === "Unintended Draining",
        ).length;
        floodingAlerts.current = alertTypes.filter(
          (t) => t === "Flooding",
        ).length;
        floodingResolvedAlerts.current = alertTypes.filter(
          (t) => t === "Flooding Resolved",
        ).length;

        setTableData(tableData);
      } else {
        totalAlerts.current = 0;
        noDrainingAlerts.current = 0;
        unintendedDrainingAlerts.current = 0;
        floodingAlerts.current = 0;
        floodingResolvedAlerts.current = 0;

        setTableData([]);
      }
      //setRefreshCounter(refreshCounter + 1); // Trigger re-render
    } catch (err) {
      console.log(err);
    }
  }, [retrieveDocs, collectionName, startUnixts, endUnixts]);

  useEffect(() => {
    validateOperation();
  }, [validateOperation]);

  const downloadCSV = () => {
    const header = ["Id", "Date Time", "Alert Type", "SSTD Id", "Camera Id"];
    const rows = tableData.map((row) => [
      row.id,
      row.datetime,
      row.alertType,
      row.sstdId,
      row.cameraId,
    ]);
    let csvContent =
      "data:text/csv;charset=utf-8," +
      header.join(",") +
      "\n" +
      rows.map((e) => e.join(",")).join("\n");

    const getFormattedNow = () => {
      const now = new Date();

      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-based
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");

      return `${year}${month}${day}${hours}${minutes}${seconds}`;
    };

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    const filename = `Alerts_${getFormattedNow()}.csv`;
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link); // Required for FF
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card size="md" shaded>
      <Card.Header
        backgroundColor={backgroundColor}
        style={{ paddingLeft: "20px", paddingBottom: "10px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Text
            size="lg"
            style={{
              textAlign: "left",
              width: "100%",
              color: "white",
            }}
          >
            {title}
          </Text>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "0px",
            }}
          >
            <Button
              appearance="appearance"
              style={{ backgroundColor: backgroundColor, color: "white" }}
              //style={{ color: { backgroundColor } }}
              onClick={() => {
                downloadCSV();
              }}
            >
              <FileDownloadIcon />
            </Button>
            <Button
              appearance="appearance"
              style={{ backgroundColor: backgroundColor, color: "white" }}
              //style={{ color: { backgroundColor } }}
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
            >
              {isCollapsed ? <ExpandOutlineIcon /> : <CollaspedOutlineIcon />}
            </Button>
          </div>
        </div>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          Alert Counts: {totalAlerts.current}
        </Text>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          No Draining: {noDrainingAlerts.current}
        </Text>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          Unintended Draining: {unintendedDrainingAlerts.current}
        </Text>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          Flooding: {floodingAlerts.current}
        </Text>
        <Text
          size="md"
          style={{
            textAlign: "left",
            width: "100%",
            color: "white",
          }}
        >
          Flooding Resolved: {floodingResolvedAlerts.current}
        </Text>
      </Card.Header>
      <Card.Body>
        {isCollapsed ? (
          <Text size="sm" style={{ textAlign: "center", width: "100%" }}>
            Click the button{" "}
            <Button
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
            >
              <ExpandOutlineIcon />
            </Button>{" "}
            to expand the table
          </Text>
        ) : (
          <Table
            height={520}
            data={getData()}
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            loading={tableLoading}
            bordered={true}
            locale={{
              emptyMessage: "",
            }}
          >
            {/* Id */}
            <Column width={60} align="center" sortable>
              <HeaderCell className={tablestyles.customHeader}>Id</HeaderCell>
              <Cell dataKey="id" className={tablestyles.customCell} />
            </Column>

            {/* Date time */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Date Time
              </HeaderCell>
              <Cell dataKey="datetime" className={tablestyles.customCell} />
            </Column>

            {/* Alert type */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Alert Type
              </HeaderCell>
              <Cell dataKey="alertType" className={tablestyles.customCell} />
            </Column>

            {/* SSTD Id */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                SSTD Id
              </HeaderCell>
              <Cell dataKey="sstdId" className={tablestyles.customCell} />
            </Column>

            {/* Camera Id */}
            <Column flexGrow={1} align="center">
              <HeaderCell className={tablestyles.customHeader}>
                Camera Id
              </HeaderCell>
              <Cell dataKey="cameraId" className={tablestyles.customCell} />
            </Column>

            {/* Edit Button */}
            <Column width={100} align="center">
              <HeaderCell>...</HeaderCell>
              <Cell style={{ padding: "3px" }}>
                {(rowData) => (
                  <Button
                    appearance="primary"
                    onClick={async () => {
                      const fileRef = await getFileRef(rowData.path);
                      const url = fileRef.url ? fileRef.url : null;
                      window.open(url, "_blank");
                      /*console.log(
                      "Edit clicked for row:",
                      JSON.stringify(rowData, null, 2),
                    );*/
                    }}
                  >
                    <ImageIcon />
                  </Button>
                )}
              </Cell>
            </Column>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default TablePanel;
